import { captureException } from "@sentry/react";

import type { ChatSaveChoices, ChatSaveDestination, ChatSaveOutcome } from "~/ai-chat/model/chat-save";
import { isProtectedBranchProblem } from "~/libraries/model/publishing-error";

interface FollowUpMutations {
  assignInfosecLevel: (resourceId: string, infosecLevelId: string) => Promise<unknown>;
  assignControlTags: (resourceId: string, controlTagIds: string[]) => Promise<unknown>;
  addToBranch: (fileRevisionId: string, displayName: string, destination: ChatSaveDestination) => Promise<unknown>;
}

type FollowUpStep = "infosec-level" | "control-tags" | "add-to-branch";

interface CreatedResource {
  resourceId: string;
  fileRevisionId: string;
  displayName: string;
}

// Every step here runs against a resource that already exists, so none may reject the save:
// a caller retry would create a duplicate. Each miss is reported instead and the user fixes it
// from the resource or system page.
export async function applyChatSaveFollowUps(
  resource: CreatedResource,
  choices: ChatSaveChoices,
  destination: ChatSaveDestination | undefined,
  mutations: FollowUpMutations,
): Promise<ChatSaveOutcome> {
  const infosecApplied = choices.infosecLevelId
    ? await succeeds("infosec-level", () => mutations.assignInfosecLevel(resource.resourceId, choices.infosecLevelId!))
    : true;
  const controlTagsApplied =
    choices.controlTagIds && choices.controlTagIds.length > 0
      ? await succeeds("control-tags", () => mutations.assignControlTags(resource.resourceId, choices.controlTagIds!))
      : true;
  if (!choices.saveToSystem || !destination) return { infosecApplied, controlTagsApplied, placedInSystem: null };
  const placement = await failureOf("add-to-branch", () =>
    mutations.addToBranch(resource.fileRevisionId, resource.displayName, destination),
  );
  if (!placement) return { infosecApplied, controlTagsApplied, placedInSystem: true };
  if (isProtectedBranchProblem(placement.error)) {
    return { infosecApplied, controlTagsApplied, placedInSystem: false, placementRefusal: "protected-branch" };
  }
  return { infosecApplied, controlTagsApplied, placedInSystem: false };
}

async function succeeds(step: FollowUpStep, run: () => Promise<unknown>): Promise<boolean> {
  return (await failureOf(step, run)) === null;
}

async function failureOf(step: FollowUpStep, run: () => Promise<unknown>): Promise<{ error: unknown } | null> {
  try {
    await run();
    return null;
  } catch (error) {
    // The user only sees a generic warning, so this is the one trace a broken step leaves.
    captureException(error, { tags: { feature: "chat-save", step } });
    return { error };
  }
}
